import React, { useEffect, useMemo, useState } from "react";
import { DataGridPro as MuiDataGridPro } from "@mui/x-data-grid-pro";

const STORAGE_PREFIX = "datagrid_columns_v1_";

const readPreferences = (key) => {
  try {
    const value = JSON.parse(localStorage.getItem(key));
    return value && typeof value === "object" ? value : {};
  } catch {
    return {};
  }
};

const normalizePath = () => {
  if (typeof window === "undefined") return "unknown";
  return window.location.pathname
    .replace(/[0-9a-f]{8}-[0-9a-f-]{27,}/gi, ":id")
    .replace(/\/\d+(?=\/|$)/g, "/:id");
};

const hash = (value) => {
  let result = 0;
  for (let index = 0; index < value.length; index += 1) {
    result = ((result << 5) - result + value.charCodeAt(index)) | 0;
  }
  return Math.abs(result).toString(36);
};

const buildStorageKey = (persistenceKey, columns) => {
  if (persistenceKey) return `${STORAGE_PREFIX}${persistenceKey}`;
  const fields = columns.map((column) => column.field).join("|");
  return `${STORAGE_PREFIX}${normalizePath()}_${hash(fields)}`;
};

export const PersistentDataGridPro = ({
  columns = [],
  persistenceKey,
  columnVisibilityModel: controlledVisibilityModel,
  onColumnVisibilityModelChange,
  pinnedColumns: controlledPinnedColumns,
  onPinnedColumnsChange,
  onColumnOrderChange,
  onColumnWidthChange,
  initialState,
  ...props
}) => {
  const storageKey = useMemo(
    () => buildStorageKey(persistenceKey, columns),
    [persistenceKey, columns]
  );
  const [preferences, setPreferences] = useState(() => readPreferences(storageKey));

  useEffect(() => {
    setPreferences(readPreferences(storageKey));
  }, [storageKey]);

  const savePreferences = (changesOrFactory) => {
    setPreferences((current) => {
      const changes = typeof changesOrFactory === "function"
        ? changesOrFactory(current)
        : changesOrFactory;
      const next = { ...current, ...changes };
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch { /* localStorage non disponibile o pieno */ }
      return next;
    });
  };

  const persistentColumns = useMemo(() => {
    const widths = preferences.widths || {};
    const order = Array.isArray(preferences.order) ? preferences.order : [];
    const position = new Map(order.map((field, index) => [field, index]));
    return columns
      .map((column, sourceIndex) => ({
        column: widths[column.field] != null
          ? { ...column, width: widths[column.field], flex: undefined }
          : column,
        sourceIndex,
      }))
      .sort((a, b) => {
        const aPosition = position.has(a.column.field) ? position.get(a.column.field) : order.length + a.sourceIndex;
        const bPosition = position.has(b.column.field) ? position.get(b.column.field) : order.length + b.sourceIndex;
        return aPosition - bPosition;
      })
      .map(({ column }) => column);
  }, [columns, preferences.order, preferences.widths]);

  const visibilityModel = controlledVisibilityModel ?? preferences.visibility;
  const pinnedColumns = controlledPinnedColumns ?? preferences.pinnedColumns;
  const mergedInitialState = {
    ...initialState,
    columns: {
      ...initialState?.columns,
      columnVisibilityModel:
        visibilityModel ?? initialState?.columns?.columnVisibilityModel,
    },
    pinnedColumns: pinnedColumns ?? initialState?.pinnedColumns,
  };

  const handleVisibilityChange = (model, details) => {
    savePreferences({ visibility: model });
    onColumnVisibilityModelChange?.(model, details);
  };

  const handlePinnedColumnsChange = (model, details) => {
    savePreferences({ pinnedColumns: model });
    onPinnedColumnsChange?.(model, details);
  };

  const handleColumnOrderChange = (params, event, details) => {
    const order = persistentColumns.map((column) => column.field);
    const oldIndex = order.indexOf(params.column.field);
    if (oldIndex >= 0) {
      order.splice(oldIndex, 1);
      order.splice(params.targetIndex, 0, params.column.field);
      savePreferences({ order });
    }
    onColumnOrderChange?.(params, event, details);
  };

  const handleColumnWidthChange = (params, event, details) => {
    savePreferences((current) => ({
      widths: {
        ...(current.widths || {}),
        [params.colDef.field]: params.width,
      },
    }));
    onColumnWidthChange?.(params, event, details);
  };

  return (
    <MuiDataGridPro
      {...props}
      columns={persistentColumns}
      initialState={mergedInitialState}
      {...(visibilityModel !== undefined ? { columnVisibilityModel: visibilityModel } : {})}
      onColumnVisibilityModelChange={handleVisibilityChange}
      {...(pinnedColumns !== undefined ? { pinnedColumns } : {})}
      onPinnedColumnsChange={handlePinnedColumnsChange}
      onColumnOrderChange={handleColumnOrderChange}
      onColumnWidthChange={handleColumnWidthChange}
    />
  );
};
